import { useMemo } from "react";
import { useApiClient } from "../apiClients";
import i18n from "../../i18n";

export type SignupPlan = "freemium" | "premium";
export type SignupMethod = "email" | "google";
export type ParentRole = "father" | "mother";

/** Étapes du tunnel — doivent rester alignées avec model/signup.go côté tc_identity. */
export const SIGNUP_STEPS = {
    LANDING_CTA: "landing_cta",
    ACCOUNT: "account",
    PARENT_ROLE: "parent_role",
    CHILDREN: "children",
    SPOUSE: "spouse",
    PAYMENT: "payment",
    CHECKOUT: "checkout",
    COMPLETED: "completed",
    ACTIVATED: "activated",
    ABANDONED: "abandoned",
} as const;

export type SignupStep = (typeof SIGNUP_STEPS)[keyof typeof SIGNUP_STEPS];

export type SignupConfig = {
    google_enabled: boolean;
    google_client_id: string;
    freemium_children_count: number;
    max_premium_children: number;
};

export type SignupAccount = {
    id: number;
    login: string;
    email?: string;
    display_name: string;
    actif: boolean;
};

export type SignupResult = {
    plan: SignupPlan;
    method: SignupMethod;
    funnel_id: string;
    parent: SignupAccount;
    children: SignupAccount[];
    spouse?: SignupAccount;
    password_required: boolean;
    email_sent: boolean;
    spouse_email_sent: boolean;
    checkout_token?: string;
    billable_user_ids?: number[];
    country?: string;
};

export type SignupPayload = {
    plan: SignupPlan;
    method: SignupMethod;
    email?: string;
    google_id_token?: string;
    parent_role?: ParentRole;
    children_count?: number;
    spouse?: { email: string } | null;
    country?: string;
    lang?: string;
    funnel_id?: string;
    /** Code de l'animateur qui a recommandé Tilmicode. Jamais bloquant. */
    code_apporteur?: string;
    /** « lien » : pré-rempli par un lien de parrainage ; « saisie » sinon. */
    code_apporteur_source?: 'lien' | 'saisie';
};

export type SetPasswordResult = {
    user_id: number;
    login: string;
    email: string;
};

export type SignupFunnelStats = {
    from: string;
    to: string;
    steps: Array<{ plan: string; step: string; funnels: number; events: number }>;
    daily: Array<{ day: string; plan: string; funnels: number; completed: number }>;
    plans: Array<{
        plan: string;
        accounts: number;
        active_accounts: number;
        google_accounts: number;
        email_accounts: number;
        children_created: number;
    }>;
};

const SIGNUP_ENDPOINTS = {
    CONFIG: "/signup/config",
    SIGNUP: "/signup",
    SET_PASSWORD: "/signup/set-password",
    TRACK: "/signup/track",
    GOOGLE_LOGIN: "/auth/google",
    FUNNEL: "/admin/signup-funnel",
} as const;

const currentLang = (): string => {
    const lang = i18n.language || window.localStorage?.getItem("lang") || "fr";
    return ["fr", "en", "ar"].includes(lang) ? lang : "fr";
};

export const useSignupApi = () => {
    const api = useApiClient("identity");

    return useMemo(
        () => ({
            /** Expose notamment le client OAuth Google, pour éviter un rebuild par environnement. */
            getConfig: async (): Promise<SignupConfig> => {
                const { data } = await api.get<SignupConfig>(SIGNUP_ENDPOINTS.CONFIG);
                return data;
            },

            /** Crée en une fois le parent, ses enfants et — en premium — le conjoint. */
            signup: async (payload: SignupPayload): Promise<SignupResult> => {
                const { data } = await api.post<SignupResult>(SIGNUP_ENDPOINTS.SIGNUP, {
                    ...payload,
                    lang: payload.lang ?? currentLang(),
                });
                return data;
            },

            /** Consomme le lien magique : définit le mot de passe et active le compte. */
            setPassword: async (token: string, password: string): Promise<SetPasswordResult> => {
                const { data } = await api.post<SetPasswordResult>(SIGNUP_ENDPOINTS.SET_PASSWORD, {
                    token,
                    password,
                });
                return data;
            },

            /** Ouvre une session à partir d'un ID token Google (compte existant). */
            loginWithGoogle: async (idToken: string): Promise<void> => {
                await api.post(SIGNUP_ENDPOINTS.GOOGLE_LOGIN, { id_token: idToken });
            },

            getFunnelStats: async (from: string, to: string): Promise<SignupFunnelStats> => {
                const { data } = await api.get<SignupFunnelStats>(SIGNUP_ENDPOINTS.FUNNEL, {
                    params: { from, to },
                });
                return data;
            },
        }),
        [api]
    );
};

/**
 * Envoi d'un événement de tunnel « best effort ».
 *
 * Trois choix délibérés :
 * - hors axios, pour ne jamais déclencher l'intercepteur 401 global qui
 *   redirigerait un visiteur anonyme ;
 * - `sendBeacon` en priorité, seul mécanisme fiable quand la page se décharge
 *   (c'est précisément le cas de l'abandon de tunnel) ;
 * - corps envoyé en `text/plain`, ce qui en fait une requête « simple » sans
 *   pré-vol CORS — un OPTIONS n'aurait pas le temps d'aboutir au déchargement.
 *   Côté serveur, gin décode le corps en JSON sans regarder le Content-Type.
 */
export const trackSignupStep = (
    baseUrl: string,
    payload: {
        funnel_id: string;
        plan: string;
        step: SignupStep;
        method?: string;
        email?: string;
        lang?: string;
        country?: string;
        metadata?: Record<string, unknown>;
    }
): void => {
    const url = `${baseUrl}${SIGNUP_ENDPOINTS.TRACK}`;
    const body = JSON.stringify({ lang: currentLang(), ...payload });

    try {
        if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
            const sent = navigator.sendBeacon(url, new Blob([body], { type: "text/plain;charset=UTF-8" }));
            if (sent) return;
        }
        void fetch(url, {
            method: "POST",
            headers: { "Content-Type": "text/plain;charset=UTF-8" },
            body,
            keepalive: true,
        }).catch(() => {
            /* le monitoring ne doit jamais casser le parcours */
        });
    } catch {
        /* idem */
    }
};
